<template>
  <v-container class="fill-height" style="opacity: 0.9" fluid>
    <v-sheet elevation="1" rounded width="100%" class="mt-16 mb-8 text-start">
      <v-banner rounded v-model="banner" single-line color="warning">
        <v-icon slot="icon" size="36">mdi-alert-outline</v-icon>
        <b>{{ $t('PROCEED WITH CAUTION') }}:</b>
        {{ $t('This page contains sensitive features that are still in beta!') }}
        <template v-slot:actions>
          <v-btn text @click="banner = false">{{ $t('Dismiss') }}</v-btn>
        </template>
      </v-banner>
      <v-row justify="space-between" class="pa-2">
        <v-col cols="12" md="6">
          <v-text-field :label="$t('Search')" prepend-icon="mdi-magnify"></v-text-field>
        </v-col>
        <v-col>
          <v-select
            v-model="selectedStore"
            :items="stores"
            :disabled="stores.length <= 1"
            @change="changeStore"
            item-text="domain"
            value="id"
            :label="$t('Select Store')"
            prepend-icon="mdi-city-variant-outline"
            return-object
          ></v-select>
        </v-col>
        <v-col align="right">
          <v-switch
            v-model="automation"
            inset
            color="green"
            @change="toggleAutomationStatus"
            :label="$t('Email Automation')"
          ></v-switch>
        </v-col>
      </v-row>
      <v-row
        justify="start"
        no-gutters
        class="py-2 pl-4 pr-12"
        style="height: 75vh; overflow-y: scroll"
      >
        <v-col>
          <v-timeline dense clipped>
            <template v-for="(item, index) in timeline">
              <v-timeline-item
                v-if="item.type === 'group'"
                class="mb-6"
                hide-dot
                :key="`group-${index}`"
              >
                <span>{{ item.timestamp | momentCalendar }}</span>
              </v-timeline-item>
              <v-timeline-item
                v-else-if="item.type === 'config'"
                class="mb-6"
                fill-dot
                small
                :key="`config-${index}`"
              >
                <template slot="icon">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon style="font-size: 16px" v-bind="attrs" v-on="on">
                        mdi-bell-cog-outline
                      </v-icon>
                    </template>
                    <span>System Notification</span>
                  </v-tooltip>
                </template>
                <v-row justify="space-between">
                  <v-col cols="7">{{ item.message }}</v-col>
                  <v-col class="text-right" cols="5">
                    {{ item.timestamp | moment }}
                  </v-col>
                </v-row>
              </v-timeline-item>
              <v-timeline-item
                v-else-if="item.type === 'manual'"
                class="mb-2"
                fill-dot
                small
                :key="`manual-${index}`"
              >
                <template slot="icon">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon style="font-size: 16px" v-bind="attrs" v-on="on">
                        mdi-account-details-outline
                      </v-icon>
                    </template>
                    <span>Manual Action</span>
                  </v-tooltip>
                </template>
                <v-row justify="space-between">
                  <v-col cols="7">
                    <span class="secondary--text font-weight-bold"> #{{ item.order }}: </span>
                    {{ item.message }}
                    <span v-if="item.type === 'registry'">
                      <a
                        :href="softwareUrl + item.regnum"
                        target="_blank"
                        class="text-decoration-none"
                      >
                        <v-icon small>mdi-open-in-new</v-icon>
                      </a>
                    </span>
                  </v-col>
                  <v-col class="text-right" cols="5">
                    {{ item.timestamp | moment }}
                  </v-col>
                </v-row>
              </v-timeline-item>
              <v-timeline-item
                v-else
                class="mb-2"
                :color="
                  item.error
                    ? 'red accent-3'
                    : item.type === 'email'
                    ? 'green accent-4'
                    : 'blue-grey lighten-2'
                "
                :icon="item.type === 'email' ? `mdi-email-fast-outline` : ''"
                :fill-dot="item.type === 'email'"
                small
                :key="`else-${index}`"
              >
                <v-row justify="space-between">
                  <v-col cols="7">
                    <span class="secondary--text font-weight-bold"> #{{ item.order }}: </span>
                    {{ item.message }}
                    <span v-if="item.type === 'registry' && !item.error">
                      <a
                        :href="softwareUrl + item.regnum"
                        target="_blank"
                        class="text-decoration-none"
                      >
                        <v-icon small>mdi-open-in-new</v-icon>
                      </a>
                    </span>
                  </v-col>
                  <v-col class="text-right" cols="5">
                    {{ item.timestamp | moment }}
                  </v-col>
                </v-row>
              </v-timeline-item>
              <v-timeline-item
                v-if="item.type === 'email'"
                class="mb-2"
                hide-dot
                :key="`email-${index}`"
              >
                <v-btn class="mx-0" @click="resendEmail(item)"> Resend Email </v-btn>
              </v-timeline-item>
            </template>
            <infinite-loading
              spinner="spiral"
              @infinite="infiniteHandler"
              :identifier="selectedStore?.id"
            ></infinite-loading>
          </v-timeline>
        </v-col>
      </v-row>
    </v-sheet>
  </v-container>
</template>

<script>
  /*   import swh from '@/assets/js/sw_helper' */
  import InfiniteLoading from 'vue-infinite-loading'
  import moment from 'moment'
  import { sendEmail } from '../services/emailService'
  import {
    getAutomationLogs,
    getAutomationStatus,
    toggleAutomation,
  } from '../services/automationService'
  import { getStoreById, getStoresProtected } from '../services/storeService'
  export default {
    props: {
      source: String,
    },
    data() {
      return {
        banner: true,
        softwareUrl: 'https://software.space-registry.org/registry/',
        selectedStore: null,
        stores: [],
        automation: false,
        timeline: [],
        loading: false,
        pageno: 0,
      }
    },
    filters: {
      moment: function (date) {
        return moment(date).format('h:mm A')
      },

      momentCalendar: function (date) {
        return moment(date)
          .calendar(null, {
            lastWeek: 'dddd',
            lastDay: '[Yesterday]',
            sameDay: '[Today]',
            nextDay: '[Tomorrow]',
            sameElse: 'MMM D',
          })
          ?.toUpperCase()
      },
    },
    methods: {
      async fetchLogs() {
        let { id: storeId } = this.$route.params

        if (!storeId) {
          this.changeStore(this.stores[0])
        }
        this.selectedStore = await getStoreById(storeId || this.stores[0]?.id)
        const { data } = (await getAutomationLogs(storeId, 0)) || {}
        this.pageno = 0
        this.timeline = data
        this.automation = (await getAutomationStatus(storeId))?.data.email || false
      },
      async changeStore(e) {
        try {
          this.$router.replace({ params: { id: e?.id } }).catch(() => {})
        } catch (error) {
          console.log(error)
        }
      },

      async resendEmail(timelineItem) {
        this.loading = true
        const { order, customer, orderId, store, upgrade } = timelineItem
        const item = {
          customer,
          orderId,
          upgrade,
          orderNumber: order,
          storeId: store,
          to: customer.email,
        }
        try {
          await sendEmail(item)
          this.showSnackbar('success', 'Email sent successfully!')
        } catch (error) {
          console.log(error)
          this.showSnackbar('error', 'Something went wrong!')
        } finally {
          this.loading = false
        }
      },

      async toggleAutomationStatus() {
        if (confirm('Are you sure you want to change the automation status?')) {
          try {
            await toggleAutomation(this.selectedStore.id, this.automation)
          } catch (err) {
            this.automation = !this.automation
          }
        } else {
          this.automation = !this.automation
        }
      },

      infiniteHandler: function ($state) {
        getAutomationLogs(this.selectedStore.id, this.pageno)
          .then(({ data } = { data: [] }) => {
            this.timeline.push(...data)
            this.pageno += 1
            $state.loaded()
            if (data.length === 0) $state.complete()
          })
          .catch(() => {})
      },
    },

    watch: {
      $route: async function () {
        try {
          this.loading = true
          await this.fetchLogs()
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
    },

    mounted: async function () {
      this.loading = true

      this.stores = await getStoresProtected()

      try {
        await this.fetchLogs()
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },

    components: { InfiniteLoading },
  }
</script>

<style>
  .theme--dark.v-input--switch .v-input--switch__track {
    color: red;
  }
</style>
